import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import { ColumnLayout } from "@/libraries/design/foundations";
import { getLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import type { ProductKeyTypes } from "@/libraries/wpl/foundations/src/data-event-props";
import { TypographyStyles } from "@/libraries/wpl/foundations/src/typography-styles";

import { ContentBlock } from "../../../components/content-block/src/content-block";

const sectionStyles = css({
    paddingBlockStart: token("space.800", "64px"),
    paddingBlockEnd: token("space.800", "64px"),
    backgroundColor: token("elevation.surface")
});

const TEMPLATES_HUB_PATH = "/templates";

const LINK_TEXT_DEFAULT_MESSAGE = "All Atlassian templates";

export interface UnifiedTemplatesNavigationSectionProps {
    product: ProductKeyTypes;
}

export const UnifiedTemplatesNavigationSection = ({
    product
}: UnifiedTemplatesNavigationSectionProps) => {
    const intl = useIntl();
    const linkText = intl.formatMessage({
        id: "unified-templates-gallery.navigation-section.link-text",
        defaultMessage: LINK_TEXT_DEFAULT_MESSAGE
    });

    return (
        <TypographyStyles>
            <div css={sectionStyles}>
                <ColumnLayout layout="10" tabletLayout="10">
                    <ContentBlock
                        titleAs="h2"
                        alignment="center"
                        title={intl.formatMessage({
                            id: "unified-templates-gallery.navigation-section.heading",
                            defaultMessage:
                                "Explore more templates to supercharge teamwork"
                        })}
                        text={intl.formatMessage({
                            id: "unified-templates-gallery.navigation-section.body",
                            defaultMessage:
                                "Empower everyone, on every team. Build, connect, and deliver your biggest ideas seamlessly across Atlassian apps using our complete collection of ready-made workflows."
                        })}
                        callToActionProps={{
                            link: {
                                text: linkText,
                                href: getLocalizedHref({
                                    locale: intl.locale,
                                    href: TEMPLATES_HUB_PATH
                                }),
                                dataEventProps: {
                                    "data-product-key": product,
                                    "data-event": "clicked",
                                    "data-event-component": "linkButton",
                                    "data-event-container":
                                        "unifiedTemplatesNavigationSection",
                                    "data-label": linkText,
                                    "data-label-english":
                                        LINK_TEXT_DEFAULT_MESSAGE
                                }
                            }
                        }}
                    />
                </ColumnLayout>
            </div>
        </TypographyStyles>
    );
};
